"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SwapContextProvider = exports.useSwapContext = exports.SwapTransactionTimeoutError = exports.SwapContext = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const decimal_js_1 = __importDefault(require("decimal.js"));
const react_1 = require("react");
const constants_1 = require("src/constants");
const utils_1 = require("src/misc/utils");
const ScreenProvider_1 = require("./ScreenProvider");
const WalletPassthroughProvider_1 = require("./WalletPassthroughProvider");
const useQuoteQuery_1 = require("src/queries/useQuoteQuery");
const useBalances_1 = require("src/hooks/useBalances");
const useAsset_1 = require("src/hooks/useAsset");
exports.SwapContext = (0, react_1.createContext)(null);
class SwapTransactionTimeoutError extends Error {
    constructor() {
        super('Transaction timed-out');
    }
}
exports.SwapTransactionTimeoutError = SwapTransactionTimeoutError;
function useSwapContext() {
    const context = (0, react_1.useContext)(exports.SwapContext);
    if (!context)
        throw new Error('Missing SwapContextProvider');
    return context;
}
exports.useSwapContext = useSwapContext;
const INITIAL_FORM = {
    fromMint: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
    toMint: constants_1.WRAPPED_SOL_MINT.toString(),
    fromValue: '',
    toValue: '',
};
const DEFAULT_FORM_PROPS = {
    swapMode: 'ExactIn',
};
const SwapContextProvider = (props) => {
    var _a, _b;
    const { displayMode, scriptDomain, formProps: originalFormProps, children, enableWalletPassthrough } = props;
    const { screen } = (0, ScreenProvider_1.useScreenState)();
    const { wallet } = (0, WalletPassthroughProvider_1.useWalletPassThrough)();
    const { data: balances, refetch: refetchBalances } = (0, useBalances_1.useBalances)();
    const isToPairFocused = (0, react_1.useRef)(false);
    const walletPublicKey = (0, react_1.useMemo)(() => { var _a; return (_a = wallet === null || wallet === void 0 ? void 0 : wallet.adapter.publicKey) === null || _a === void 0 ? void 0 : _a.toString(); }, [wallet === null || wallet === void 0 ? void 0 : wallet.adapter.publicKey]);
    const formProps = (0, react_1.useMemo)(() => (Object.assign(Object.assign({}, DEFAULT_FORM_PROPS), originalFormProps)), [originalFormProps]);
    const [form, setForm] = (0, react_1.useState)({
        fromMint: (_a = formProps === null || formProps === void 0 ? void 0 : formProps.initialInputMint) !== null && _a !== void 0 ? _a : 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
        toMint: (_b = formProps === null || formProps === void 0 ? void 0 : formProps.initialOutputMint) !== null && _b !== void 0 ? _b : constants_1.WRAPPED_SOL_MINT.toString(),
        fromValue: '',
        toValue: '',
    });
    const { data: fromTokenInfo } = (0, useAsset_1.useAsset)(form.fromMint);
    const { data: toTokenInfo } = (0, useAsset_1.useAsset)(form.toMint);
    (0, react_1.useEffect)(() => {
        if (formProps.fixedMint) {
            if (formProps.fixedMint !== formProps.initialInputMint && formProps.fixedMint !== formProps.initialOutputMint) {
                console.error('fixedMint is not the same as the initial input or output mint');
            }
        }
    }, [formProps.fixedMint, formProps.initialInputMint, formProps.initialOutputMint]);
    const [errors, setErrors] = (0, react_1.useState)({});
    // Set value given initial amount
    const setupInitialAmount = (0, react_1.useCallback)(() => {
        if (!(formProps === null || formProps === void 0 ? void 0 : formProps.initialAmount) || !fromTokenInfo || !toTokenInfo)
            return;
        const toUiAmount = () => {
            if (!formProps.initialAmount) {
                return;
            }
            if (!fromTokenInfo)
                return;
            const value = new decimal_js_1.default(formProps.initialAmount).div(Math.pow(10, fromTokenInfo.decimals)).toFixed();
            return value;
        };
        setTimeout(() => {
            setForm((prev) => { var _a; return (Object.assign(Object.assign({}, prev), { fromValue: (_a = toUiAmount()) !== null && _a !== void 0 ? _a : '' })); });
        }, 0);
    }, [formProps.initialAmount, fromTokenInfo, toTokenInfo]);
    (0, react_1.useEffect)(() => {
        setupInitialAmount();
    }, [formProps.initialAmount, setupInitialAmount]);
    const debouncedForm = (0, utils_1.useDebounce)(form, 250);
    const debouncePending = (0, react_1.useMemo)(() => JSON.stringify(form) !== JSON.stringify(debouncedForm), [form, debouncedForm]);
    const amount = (0, react_1.useMemo)(() => {
        var _a, _b;
        if (!fromTokenInfo || !toTokenInfo) {
            return BigInt(0);
        }
        if (isToPairFocused.current === true) {
            return (_a = (0, utils_1.parseUiAmount)(debouncedForm.toValue, toTokenInfo)) !== null && _a !== void 0 ? _a : BigInt(0);
        }
        else {
            return (_b = (0, utils_1.parseUiAmount)(debouncedForm.fromValue, fromTokenInfo)) !== null && _b !== void 0 ? _b : BigInt(0);
        }
    }, [debouncedForm.fromValue, debouncedForm.toValue, fromTokenInfo, toTokenInfo]);
    const [txStatus, setTxStatus] = (0, react_1.useState)(undefined);
    const { data: ogQuoteResponseMeta, isFetching: loading, error: quoteError, refetch: refresh, errorUpdatedAt, dataUpdatedAt, isSuccess, isError, } = (0, useQuoteQuery_1.useQuoteQuery)({
        inputMint: debouncedForm.fromMint,
        outputMint: debouncedForm.toMint,
        amount: amount.toString(),
        taker: walletPublicKey,
        swapMode: isToPairFocused.current ? 'ExactOut' : 'ExactIn',
        referralAccount: formProps.referralAccount,
        referralFee: formProps.referralFee,
        excludeDexes: formProps.excludeDexes,
    }, 
    // Stop refetching when transaction is in progress
    !txStatus);
    const balance = (0, react_1.useMemo)(() => {
        var _a;
        if (!balances)
            return 0;
        return ((_a = balances[form.fromMint]) === null || _a === void 0 ? void 0 : _a.uiAmount) || 0;
    }, [balances, form.fromMint]);
    (0, react_1.useEffect)(() => {
        if (quoteError) {
            if (typeof quoteError === 'string') {
                setErrors({
                    fromValue: { title: quoteError, message: '' },
                });
                return;
            }
            setErrors({
                fromValue: { title: 'Error fetching route. Try changing your input', message: '' },
            });
            return;
        }
        if (form.fromValue && new decimal_js_1.default(form.fromValue).gt(balance)) {
            setErrors({
                fromValue: { title: `Insufficient ${fromTokenInfo === null || fromTokenInfo === void 0 ? void 0 : fromTokenInfo.symbol}`, message: '' },
            });
            return;
        }
        setErrors({});
    }, [quoteError, balance, form.fromValue, fromTokenInfo]);
    const lastRefreshTimestamp = (0, react_1.useMemo)(() => {
        if (loading) {
            return new Date().getTime();
        }
        if (isError) {
            return new Date(errorUpdatedAt).getTime();
        }
        if (isSuccess) {
            return new Date(dataUpdatedAt).getTime();
        }
        return undefined;
    }, [loading, errorUpdatedAt, dataUpdatedAt, isError, isSuccess]);
    const [quoteResponseMeta, setQuoteResponseMeta] = (0, react_1.useState)(null);
    (0, react_1.useEffect)(() => {
        if (!ogQuoteResponseMeta) {
            setQuoteResponseMeta(null);
            return;
        }
        // the UI sorts the best route depending on ExactIn or ExactOut
        setQuoteResponseMeta(ogQuoteResponseMeta);
    }, [ogQuoteResponseMeta]);
    (0, react_1.useEffect)(() => {
        if (!form.fromValue && !form.toValue && !quoteResponseMeta) {
            setForm((prev) => (Object.assign(Object.assign({}, prev), { fromValue: '', toValue: '' })));
            return;
        }
        setForm((prev) => {
            const newValue = Object.assign({}, prev);
            if (!fromTokenInfo || !toTokenInfo)
                return prev;
            const { outAmount, inAmount } = (quoteResponseMeta === null || quoteResponseMeta === void 0 ? void 0 : quoteResponseMeta.quoteResponse) || {};
            if (!isToPairFocused.current) {
                newValue.toValue = outAmount ? (0, utils_1.formatUiAmount)(outAmount, toTokenInfo) : '';
            }
            else {
                newValue.fromValue = inAmount ? (0, utils_1.formatUiAmount)(inAmount, fromTokenInfo) : '';
            }
            return newValue;
        });
    }, [form.fromValue, form.toValue, fromTokenInfo, quoteResponseMeta, toTokenInfo]);
    const [lastSwapResult, setLastSwapResult] = (0, react_1.useState)(null);
    const reset = (0, react_1.useCallback)(({ resetValues } = { resetValues: false }) => {
        if (resetValues) {
            setForm(INITIAL_FORM);
            setupInitialAmount();
        }
        else {
            setForm((prev) => (Object.assign(Object.assign({}, prev), { toValue: '' })));
        }
        setQuoteResponseMeta(null);
        setErrors({});
        setLastSwapResult(null);
        setTxStatus(undefined);
        refetchBalances();
    }, [refetchBalances, setupInitialAmount]);
    // onFormUpdate callback
    (0, react_1.useEffect)(() => {
        if (typeof window.Jupiter.onFormUpdate === 'function') {
            window.Jupiter.onFormUpdate(form);
        }
    }, [form]);
    // onFormUpdate callback
    (0, react_1.useEffect)(() => {
        if (typeof window.Jupiter.onScreenUpdate === 'function') {
            window.Jupiter.onScreenUpdate(screen);
        }
    }, [screen]);
    return ((0, jsx_runtime_1.jsx)(exports.SwapContext.Provider, { value: {
            form,
            setForm,
            errors,
            setErrors,
            fromTokenInfo,
            toTokenInfo,
            quoteResponseMeta,
            setQuoteResponseMeta,
            // onSubmit,
            lastSwapResult,
            setLastSwapResult,
            reset,
            refresh,
            loading: loading || debouncePending,
            quoteError,
            lastRefreshTimestamp,
            isToPairFocused,
            displayMode,
            formProps,
            scriptDomain,
            swapping: {
                txStatus,
            },
            setTxStatus,
            enableWalletPassthrough,
        }, children: children }));
};
exports.SwapContextProvider = SwapContextProvider;
